import { Dropdown } from "bootstrap";

export default class ThemeDropdown {
    // ** Object references **/
    private dropdown: HTMLElement;
    private dropdownButton: HTMLElement;
    private dropdownButtonContent: HTMLElement;
    private dropdownItems: NodeListOf<HTMLElement>;

    //** Number of items selected */
    selectedOptions: number;
    //** Default text to show */
    private defaultTitle: string;

    //** Singular or Multi Dropdown */
    private isMultiDropdown: boolean;

    //** Can Unselect Items that are checked */
    private unableToUnselectItems: boolean

    //** Should all items be selected by default */
    private allSelectedByDefault: boolean;

    //** Is this a form field? */
    private isFormField: boolean;

    //** DefaultValue Form Field */
    private formField: HTMLTextAreaElement;

    /**
     * @param dropdownID - Dropdown Identifier
     * @param isMultiDropdown - Is this a multi dropdown?
     * @param allItemsSelected - Should all items be selected by default?
     * @param unableToUnselectItems - Should items be unable to be unselected?
     * @param isFormField - If this is used in a form, then the dropdown will populate the hidden defaultvalue field
     */
    constructor(
        dropdownID: string,
        isMultiDropdown: boolean,
        allItemsSelected: boolean,
        unableToUnselectItems: boolean = false,
        isFormField: boolean = false,
    ) {
        this.dropdown = document.getElementById(dropdownID);

        if (this.dropdown) {
            this.dropdownButton = this.dropdown.querySelector('.btn') as HTMLElement;
            this.dropdownButtonContent = this.dropdownButton.querySelector('.value');
            this.dropdownItems = this.dropdown.querySelectorAll('.dropdown-item');
            this.defaultTitle = this.dropdownButtonContent?.textContent;
            this.isMultiDropdown = isMultiDropdown;
            this.allSelectedByDefault = allItemsSelected;
            this.unableToUnselectItems = unableToUnselectItems;
            this.isFormField = isFormField;
            this.AddEventListeners();

            if (this.allSelectedByDefault) {
                this.SelectAll();
                this.UpdateSelectedIds(this.dropdown);
            }

            if (this.isFormField) {
                this.formField = this.dropdown.querySelector('.f-field textarea');
            }
        }
    }

    // ** Reset the dropdown **/
    Reset(): void {
        if (!this.dropdownItems || this.dropdownItems.length == 0)
            return;
        this.dropdownItems.forEach(item => {
            const dropdownIcon: HTMLElement = item.querySelector("span.dot-icon");

            item.classList.remove("selected");

            if (dropdownIcon) {
                dropdownIcon.classList.remove("dot-stroke-dropdown-up");

                if (!dropdownIcon.classList.contains("dot-stroke-dropdown-down"))
                    dropdownIcon.classList.add("dot-stroke-dropdown-down");
            }
        });
        if (this.dropdownButtonContent) {

            this.dropdownButtonContent.innerText = this.defaultTitle;
            this.RemoveSelectedFilterButtons();
        }
    }

    // ** Selection of all items **/
    SelectAll(): void {
        this.dropdownItems.forEach(item => {
            const dropdownIcon: HTMLElement = item.querySelector("span.dot-icon");

            item.classList.add("selected");

            if (dropdownIcon) {
                dropdownIcon.classList.remove("dot-stroke-dropdown-down");

                if (!dropdownIcon.classList.contains("dot-stroke-dropdown-up"))
                    dropdownIcon.classList.add("dot-stroke-dropdown-up");
            }
        });
    }

    // ** Event Listeners **/
    AddEventListeners(): void {
        this.dropdownItems.forEach((item) => {
            item.addEventListener('click', (event: Event) => {
                this.HandleSelect(item, event);
                this.dropdown.focus()
            });

            if (this.isMultiDropdown) {
                item.addEventListener('keydown', (event: KeyboardEvent) => {
                    if (event.key === "Enter")
                        this.HandleSelect(item, event);
                        this.dropdown.focus()
                });
            }
        });
    }

    // ** Events **/
    UpdateSelectedIds(dropdown: HTMLElement, _ignoreEvents: boolean = false): void {
        const selectedDataIds: NodeListOf<Element> = dropdown.querySelectorAll(".selected");

        if (!selectedDataIds) return;

        const idArray: any = Array.from(selectedDataIds)
            .map((elem: HTMLElement) => elem.getAttribute('data-id'))
            .filter(id => id !== null && id !== '');
        const idStr: string = idArray.join(',');

        this.dropdownButton.setAttribute('data-selected-ids', idStr);
        this.selectedOptions = idArray.length;

        if (this.isFormField && this.formField) {
            if (this.isMultiDropdown) {
                this.formField.value = idStr;
            } else {
                this.formField.value = idArray[0] || '';
            }
        }
    }

    UpdateButtonLabel(dropdown: HTMLElement): void {
        const selectedDataItems: NodeListOf<HTMLDivElement> = dropdown.querySelectorAll('.selected:not(.btn):not(.dropdown-menu)');
        const selectedItemsWrapper = this.dropdownButton.querySelector('.selected-items');
        const existingIcon = this.dropdownButton.querySelector('.fa');
        if (existingIcon) {
            existingIcon.remove();
        }
        if (this.isMultiDropdown) {
            let innerHTML = "";
            selectedDataItems.forEach(item => {
                innerHTML += `
                <button type="button" class='selected-item-btn' data-id='${item.getAttribute('data-id')}'>
                <span class="visually-hidden">Remove filter</span>
                <span class="label">${item.getAttribute('data-name')}</span>
                <span class="dot-icon dot-stroke-close"></span>
                </button>
                `;
            })
            if (this.selectedOptions > 0) {
                this.dropdownButtonContent.innerText = ''
            } else {
                this.dropdownButtonContent.innerText = this.defaultTitle;
            }
            if (selectedItemsWrapper) selectedItemsWrapper.innerHTML = innerHTML;
            this.AttachClickEventToSelectedOption();
        } else {
            if (this.selectedOptions === 0) {
                this.dropdownButtonContent.innerText = this.defaultTitle;
                if (selectedItemsWrapper) selectedItemsWrapper.innerHTML = "";
            } else if (this.selectedOptions == 1) {
                if (!selectedDataItems[0].getAttribute("data-name")) return;
                const selectedItemValue = selectedDataItems[0].getAttribute("data-id");
                const selectedItemName = selectedDataItems[0].getAttribute("data-name");
                this.dropdownButtonContent.innerText = selectedItemName;
                this.dropdownButton.setAttribute('data-itemid', selectedItemValue);
            }
        }

        if (this.dropdownItems.length == 0) {
            this.dropdownButton.classList.add('disabled');
        } else if (this.dropdownButton.classList.contains('disabled')) {
            this.dropdownButton.classList.remove('disabled');
        }
    }
    HandleSelect(item: HTMLElement, event: Event) {
        if (!this.isMultiDropdown) {
            // If it's not a multi dropdown, uncheck all other items
            this.dropdownItems.forEach(otherItem => {
                if (otherItem !== item) {
                    otherItem.classList.remove("selected");
                    otherItem.ariaChecked = "false";
                }
            });
        } else {
            event.preventDefault();
            event.stopPropagation();
        }

        if (item.classList.contains("selected")) {
            if (this.unableToUnselectItems) return;
            item.classList.remove("selected");
            item.ariaChecked = "false";
        } else {
            item.classList.add("selected");
            item.ariaChecked = "true";
        }
        this.UpdateSelectedIds(this.dropdown);
        this.dropdownButtonContent && this.UpdateButtonLabel(this.dropdown);
        this.isMultiDropdown && this.UpdateClearButton();
    }

    // Helper methods to move to the next and previous items
    MoveToNextItem(currentIndex: number): void {
        if (currentIndex < 0) {
            currentIndex = this.dropdownItems.length - 1;
        } else if (currentIndex >= this.dropdownItems.length) {
            currentIndex = 0;
        }
        this.dropdownItems[currentIndex].focus();
    }

    MoveToPrevItem(currentIndex: number): void {
        if (currentIndex < 0) {
            currentIndex = this.dropdownItems.length - 1;
        } else if (currentIndex >= this.dropdownItems.length) {
            currentIndex = 0;
        }
        this.dropdownItems[currentIndex].focus();
    }

    AttachClickEventToSelectedOption(): void {
        const selectedFilterButtons = this.dropdown.querySelectorAll<HTMLButtonElement>('.selected-item-btn');
        if (!selectedFilterButtons || selectedFilterButtons.length === 0) return;

        const dropdownMenu = this.dropdown.querySelector('.dropdown-menu');
        selectedFilterButtons.forEach(btn => {
            btn.addEventListener("click", (e: Event) => {
                e.preventDefault();
                e.stopImmediatePropagation();
                const dropdownElement = btn.closest<HTMLButtonElement>('.theme-dropdown .dropdown');
                if (dropdownElement) {
                    const dropdown = new Dropdown(dropdownElement);
                    dropdownElement.classList.add('hide');
                    dropdown.hide();
                }

                if (e.target as HTMLElement === btn.querySelector<HTMLElement>('.dot-stroke-close')) {
                    this.OnSelectedItemRemove.bind(this)(btn, dropdownMenu);
                }
            });
            btn.addEventListener("keydown", (e: KeyboardEvent) => {
                if (e.key == "Enter") {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                    this.OnSelectedItemRemove.bind(this)(btn, dropdownMenu);
                }
            });
        });
    }
   
    OnSelectedItemRemove(btn: HTMLButtonElement, dropdownMenu: HTMLElement) {
        this.dropdownButton.classList.add('selected');
        dropdownMenu.classList.add('selected');
        const itemId = btn.getAttribute('data-id');
        const selectedItem = this.dropdown.querySelector(`.selected[data-id="${itemId}"]`) as HTMLElement;
        selectedItem.click();
    }
    CheckInRange(index: number, start: number, end: number) {
        const rangeStart = start < end ? start : end;
        const rangeEnd = start < end ? end : start;

        return index >= rangeStart && index <= rangeEnd;
    }
    UpdateClearButton() {
        const clearButtons = document.querySelectorAll<HTMLButtonElement>('.clear-button:not(.location-map--header .clear-button)');
        const selectedButtons = Array.from(document.querySelectorAll<HTMLElement>('.selected-items .selected-item-btn, .offcanvas-filter__inner .dropdown-item.selected'));
        clearButtons.forEach(btn => {
            btn.classList.toggle('active', selectedButtons.length > 0);
            btn.disabled = selectedButtons.length === 0;
        });
    }
    RemoveSelectedFilterButtons() {
        const selectedFilterWrappers = document.querySelectorAll('.selected-items');
        selectedFilterWrappers.forEach(wrapper => wrapper.innerHTML = '');
    }
}